'use client'

import type { ButtonProps, InternalButtonProps } from '../types'

import {
  type CSSProperties,
  type ReactNode,
  type Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { mergeRefs } from '#lib/merge-refs'

import { Button } from '../button'
import { TEXT_FADE_IN_ANIMATION, WIDTH_TRANSITION } from '../styles'

/**
 * Animation props requiring at least one animation to be enabled.
 */
type AnimationProps =
  | {
      /** Animate width changes when content changes */
      animateWidth?: boolean
      /** Animate text fade-in when children changes (default: true) */
      animateText?: true
    }
  | {
      animateWidth: true
      animateText?: boolean
    }

/** @public — component prop type */
export type AnimatedButtonProps = ButtonProps &
  AnimationProps & {
    ref?: Ref<HTMLButtonElement>
  }

/** Get text content for change detection */
function getChildrenKey(children: ReactNode): string {
  return typeof children === 'string'
    ? children
    : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
      typeof children === 'number'
      ? String(children)
      : ''
}

/**
 * Button wrapper that adds smooth width and/or text fade-in animations.
 *
 * ## How it works:
 *
 * **Width animation** (`animateWidth`):
 * - Measures the button's natural width whenever content changes
 * - Uses a CSS transition to smoothly animate between widths
 * - On first render, captures initial width without animating
 * - Technique: temporarily sets `width: fit-content` to measure, then restores previous width
 *   so the transition animates from old → new width
 *
 * **Text animation** (`animateText`):
 * - Increments `labelKey` when children change, which is passed to Button
 * - Button uses this key to re-trigger a CSS fade-in animation on the label
 * - Skips animation on first render (labelKey starts at 0)
 *
 * **Ref handling**:
 * - Needs an internal ref (`innerRef`) to measure button width
 * - Merges with external `ref` so parent components can also access the button
 */
function AnimatedButton({
  animateWidth = false,
  animateText = true,
  style,
  ref,
  ...props
}: AnimatedButtonProps) {
  const innerRef = useRef<HTMLButtonElement>(null)
  const isFirstRender = useRef(true)
  const [width, setWidth] = useState<number | null>(null)
  // Incrementing key triggers re-animation of the label in the Button component
  const [labelKey, setLabelKey] = useState(0)
  const prevChildrenKey = useRef<string>('')

  const currentChildrenKey = getChildrenKey(props.children)

  // Detect children changes and increment labelKey to trigger text fade-in
  useEffect(() => {
    if (
      animateText &&
      prevChildrenKey.current !== '' &&
      prevChildrenKey.current !== currentChildrenKey
    ) {
      setLabelKey((k) => k + 1)
    }
    prevChildrenKey.current = currentChildrenKey
  }, [animateText, currentChildrenKey])

  // Measure and animate width changes when content updates
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger re-measurement on content change
  useEffect(() => {
    if (!animateWidth) {
      return
    }
    const button = innerRef.current
    if (!button) {
      return
    }

    // First render: capture initial width without animating
    if (isFirstRender.current) {
      isFirstRender.current = false
      setWidth(button.offsetWidth)
      return
    }

    // Measure natural width: temporarily set to fit-content, read offsetWidth, restore.
    // fit-content gives the intrinsic content width regardless of flex layout context
    // (using 'auto' would stretch in flex-col containers with align-items: stretch).
    const prevWidth = button.style.width
    button.style.width = 'fit-content'
    setWidth(button.offsetWidth)
    button.style.width = prevWidth
  }, [
    animateWidth,
    currentChildrenKey,
    props.isLoading,
    props.leftSection,
    props.rightSection,
  ])

  const mergedRef = useMemo(() => mergeRefs(innerRef, ref), [ref])

  const animatedStyle: CSSProperties | undefined =
    animateWidth && width !== null
      ? { ...style, overflow: 'hidden', transition: WIDTH_TRANSITION, width }
      : style

  const labelStyle: CSSProperties | undefined =
    animateText && labelKey > 0
      ? { animation: TEXT_FADE_IN_ANIMATION }
      : undefined

  const buttonProps: InternalButtonProps = {
    ...props,
    labelKey: animateText ? labelKey : undefined,
    labelStyle,
    style: animatedStyle,
  }

  return <Button {...buttonProps} ref={mergedRef} />
}
AnimatedButton.displayName = 'AnimatedButton'

export { AnimatedButton }
